const DEFAULT_FLOW_COLOR = 'grey';
const DEFAULT_LOG_COLOR = '#6b7280';
const DEFAULT_ICON = 'mdi-circle';
const DEFAULT_LOG_GRADIENT = 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)';

export const JSON_CONDITION_OPERATOR_TOKENS = Object.freeze({
	equals: '=',
	not_equals: '≠',
	greater_than: '>',
	less_than: '<',
	greater_equal: '≥',
	less_equal: '≤',
	contains: '⊃',
	not_contains: '⊅',
	starts_with: 'starts',
	ends_with: 'ends',
	regex: '~ regex',
	is_empty: 'empty',
	is_not_empty: '≠ empty',
	exists: '∃',
	not_exists: '∄',
	array_empty: '[] empty',
	array_not_empty: '[] ≠ empty',
	array_contains: '[] ⊃',
	array_length_min: 'len ≥',
	array_length_max: 'len ≤',
	array_length_equals: 'len =',
	array_all_unique: 'all unique',
	is_true: 'true',
	is_false: 'false',
	type_is: 'type:',
	one_of: '∈',
	not_one_of: '∉',
	format: 'fmt:',
	equals_field: '= field',
	greater_than_field: '> field',
	less_than_field: '< field',
});

// Entry ที่ label ทั้งสามช่องและ icon ทั้งสองช่องเป็นค่าเดียวกัน
function uniformNodeInfo(label, icon, flowColor, logColor, logGradient) {
	return Object.freeze({
		flowLabel: label,
		logLabel: label,
		workflowLabel: label,
		flowIcon: icon,
		logIcon: icon,
		flowColor,
		logColor,
		logGradient,
	});
}

// Entry ที่ใช้ฝั่ง flow เท่านั้น (ไม่มีข้อมูลฝั่ง log)
function flowOnlyNodeInfo(label, icon, flowColor) {
	return Object.freeze({
		flowLabel: label,
		workflowLabel: label,
		flowIcon: icon,
		flowColor,
	});
}

// Entry ที่ใช้ฝั่ง log เท่านั้น
function logOnlyNodeInfo(label, icon, logColor, logGradient) {
	return Object.freeze({
		logLabel: label,
		logIcon: icon,
		logColor,
		logGradient,
	});
}

export const NODE_TYPE_INFO = Object.freeze({
	messageInput: Object.freeze({
		flowLabel: 'Message Input',
		logLabel: 'Message Input',
		workflowLabel: 'Message Input',
		flowIcon: 'mdi-chat',
		logIcon: 'mdi-message-text',
		flowColor: 'green',
		logColor: '#22c55e',
	}),
	webhookTrigger: uniformNodeInfo('Webhook Trigger', 'mdi-webhook', 'purple', '#a855f7', 'linear-gradient(135deg, #a855f7 0%, #9333ea 100%)'),
	model: uniformNodeInfo('AI Model', 'mdi-robot', 'blue', '#3b82f6', 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'),
	aiModel: Object.freeze({
		logLabel: 'AI Model Processing',
		workflowLabel: 'AI Model',
		logIcon: 'mdi-robot',
		logColor: '#3b82f6',
	}),
	guardrailAI: flowOnlyNodeInfo('Guardrail AI', 'mdi-shield-check', 'green'),
	intentAI: flowOnlyNodeInfo('Intent AI Model', 'mdi-target-variant', 'purple'),
	intentClassifier: flowOnlyNodeInfo('Intent Classifier', 'mdi-sort-variant', 'purple'),
	intent: Object.freeze({
		flowLabel: 'Intent',
		logLabel: 'Intent Router',
		workflowLabel: 'Intent',
		flowIcon: 'mdi-crosshairs-question',
		logIcon: 'mdi-router',
		flowColor: 'purple',
		logColor: '#8b5cf6',
		logGradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
	}),
	smartIntent: uniformNodeInfo('Smart Intent', 'mdi-robot-confused', 'purple', '#4f46e5', 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)'),
	advancedRouter: uniformNodeInfo('Advanced Router', 'mdi-source-branch', 'purple', '#8b5cf6', 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'),
	jsonCondition: Object.freeze({
		label: 'JSON Condition',
		icon: 'mdi-code-json',
		color: 'indigo',
		flowLabel: 'JSON Condition',
		logLabel: 'JSON Condition',
		workflowLabel: 'JSON Condition',
		flowIcon: 'mdi-code-json',
		logIcon: 'mdi-code-json',
		flowColor: 'teal',
		logColor: '#06b6d4',
		logGradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
	}),
	apiTrigger: uniformNodeInfo('API Trigger', 'mdi-rocket-launch', 'blue', '#0ea5e9', 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)'),
	orchestrator: uniformNodeInfo('Orchestrator', 'mdi-hub', 'pink', '#ec4899', 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)'),
	agent: uniformNodeInfo('Agent', 'mdi-robot', 'teal', '#14b8a6', 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)'),
	tool: uniformNodeInfo('Tool', 'mdi-tools', 'orange', '#f97316', 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'),
	'agent-tool': uniformNodeInfo('Agent Tool', 'mdi-tools', 'orange', '#f97316', 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'),
	'agent-find-tools': uniformNodeInfo('Agent (Find tools)', 'mdi-robot', 'teal', '#14b8a6', 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)'),
	'agent-summary': uniformNodeInfo('Agent (Summary)', 'mdi-robot', 'teal', '#14b8a6', 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)'),
	'agent-validation': uniformNodeInfo('Agent (Validation)', 'mdi-robot', 'teal', '#14b8a6', 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)'),
	flowTool: uniformNodeInfo('Flow Tool', 'mdi-sitemap', 'indigo', '#06b6d4', 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)'),
	flowInput: uniformNodeInfo('Flow Input', 'mdi-import', 'green', '#22c55e', 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'),
	flowOutput: uniformNodeInfo('Flow Output', 'mdi-export', 'red', '#ef4444', 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'),
	contactAgent: uniformNodeInfo('Contact Agent', 'mdi-account-supervisor', 'red', '#dc2626', 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'),
	switch: flowOnlyNodeInfo('Switch', 'mdi-arrow-decision', 'red'),
	condition: flowOnlyNodeInfo('Condition', 'mdi-swap-horizontal-bold', 'green'),
	delay: flowOnlyNodeInfo('Delay', 'mdi-timer', 'blue'),
	documents: uniformNodeInfo('Documents', 'mdi-file-document-multiple', 'blue', '#0369a1', 'linear-gradient(135deg, #0369a1 0%, #075985 100%)'),
	api: flowOnlyNodeInfo('API Call', 'mdi-api', 'amber'),
	response: Object.freeze({
		flowLabel: 'Response',
		logLabel: 'Message Output',
		workflowLabel: 'Response',
		flowIcon: 'mdi-message-reply-text',
		logIcon: 'mdi-message-reply-text',
		flowColor: 'red',
		logColor: '#f97316',
	}),
	databaseIntegration: Object.freeze({
		flowLabel: 'Database Integration',
		logLabel: 'DB Integration',
		workflowLabel: 'Database Integration',
		flowIcon: 'mdi-database-search',
		logIcon: 'mdi-database-search',
		flowColor: 'green',
		logColor: '#16a34a',
		logGradient: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
	}),
	apiknowledge: Object.freeze({
		flowLabel: 'Knowledge API',
		logLabel: 'API Knowledge',
		workflowLabel: 'Knowledge API',
		flowIcon: 'mdi-cloud',
		logIcon: 'mdi-rocket-launch',
		flowColor: 'indigo',
		logColor: '#ff6c37',
		logGradient: 'linear-gradient(135deg, #ff6c37 0%, #ff5722 100%)',
	}),
	products: flowOnlyNodeInfo('Products', 'mdi-package-variant', 'amber'),
	webhook: flowOnlyNodeInfo('Webhook', 'mdi-webhook', 'green'),
	email: flowOnlyNodeInfo('Email', 'mdi-email', 'red'),
	sharepoint: flowOnlyNodeInfo('SharePoint', 'mdi-microsoft-sharepoint', 'green'),
	merge: Object.freeze({
		flowLabel: 'Merge',
		logLabel: 'Merge Results',
		workflowLabel: 'Merge',
		flowIcon: 'mdi-call-merge',
		logIcon: 'mdi-merge',
		flowColor: 'blue',
		logColor: '#06b6d4',
		logGradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
	}),
	interestOptimizer: logOnlyNodeInfo('Interest Optimizer', 'mdi-calculator', '#f59e0b', 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'),
	loanInterest: logOnlyNodeInfo('Loan Interest', 'mdi-home-analytics', '#10b981', 'linear-gradient(135deg, #10b981 0%, #059669 100%)'),
	embedding: logOnlyNodeInfo('Vector Embedding', 'mdi-code-array', '#14b8a6', 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)'),
	queryRewriter: Object.freeze({
		flowLabel: 'Query Rewriter',
		flowIcon: 'mdi-message-text-clock-outline',
		flowColor: 'deep-purple',
		logLabel: 'Query Rewriter',
		logIcon: 'mdi-message-text-clock-outline',
		logColor: '#7c3aed',
		logGradient: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
	}),
});

function getNodeTypeInfo(nodeType) {
	return NODE_TYPE_INFO[nodeType] || {};
}

export function getJsonConditionOperatorToken(operatorType) {
	return JSON_CONDITION_OPERATOR_TOKENS[operatorType] || '';
}

export function getFlowNodeTypeColor(nodeType) {
	return getNodeTypeInfo(nodeType).flowColor || DEFAULT_FLOW_COLOR;
}

export function getFlowNodeTypeIcon(nodeType) {
	const info = getNodeTypeInfo(nodeType);
	return info.flowIcon || info.logIcon || DEFAULT_ICON;
}

export function getFlowNodeTypeLabel(nodeType) {
	const info = getNodeTypeInfo(nodeType);
	return info.flowLabel || info.workflowLabel || info.logLabel || nodeType;
}

export function getLogNodeName(nodeType) {
	const info = getNodeTypeInfo(nodeType);
	return info.logLabel || info.flowLabel || info.workflowLabel || nodeType;
}

export function getLogNodeIcon(nodeType) {
	const info = getNodeTypeInfo(nodeType);
	return info.logIcon || info.flowIcon || DEFAULT_ICON;
}

export function getLogNodeTypeColor(nodeType) {
	return getNodeTypeInfo(nodeType).logColor || DEFAULT_LOG_COLOR;
}

export function getLogNodeGradient(nodeType) {
	return getNodeTypeInfo(nodeType).logGradient || DEFAULT_LOG_GRADIENT;
}

export function getChatWorkflowLabel(nodeType) {
	if (!nodeType) {
		return '-';
	}

	const info = getNodeTypeInfo(nodeType);
	return info.workflowLabel || info.flowLabel || info.logLabel || nodeType;
}
